import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  TablePagination,
  CircularProgress,
  LinearProgress,
  Divider
} from '@mui/material';
import { Search, Close } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  QuestionType,
  QuestionImportance,
  Question,
  Chapter,
  questionPaperService
} from '../../services/questionPaperService';
import { useDebounce } from '../../hooks/useDebounce';
import MathTextRenderer from '../shared/MathTextRenderer';
import AnswerBadge from '../shared/AnswerBadge';
import { elegantMenuProps } from '../../utils/menuStyles';

const ROMAN_NUMERALS = ['i','ii','iii','iv','v','vi','vii','viii','ix','x','xi','xii','xiii','xiv','xv'];

// ============================================================================
// Bank-Wide Search Dialog
//
// Surfaces the /api/question-paper/question-banks/:id/questions endpoint —
// which uses the Mongo text index on Question — so a teacher can search the
// whole bank instead of one chapter at a time. Kept as its own component so
// the (already-large) QuestionPaperPage doesn't grow further.
// ============================================================================

interface BankWideSearchDialogProps {
  open: boolean;
  onClose: () => void;
  questionBankId: string;
  bankLabel?: string;
  chapters: Chapter[];                 // for the optional in-bank chapter filter
  // When provided, the dialog switches into multi-select mode: checkbox per row,
  // a sticky DialogActions footer with "Add N selected", and the callback fires
  // on confirm. Used by the Paper Builder; the standalone browse mode (no
  // onAddSelected) is fine for read-only inspection.
  onAddSelected?: (questions: Question[]) => void;
  // Optional id-set the caller already added — those rows render as disabled
  // so users can't double-add the same question.
  alreadyAddedIds?: Set<string>;
}

const IMPORTANCE_COLOR: Record<QuestionImportance, string> = {
  [QuestionImportance.HIGH]:   '#ef4444',
  [QuestionImportance.MEDIUM]: '#f59e0b',
  [QuestionImportance.LOW]:    '#22c55e'
};



const BankWideSearchDialog: React.FC<BankWideSearchDialogProps> = ({
  open,
  onClose,
  questionBankId,
  bankLabel,
  chapters,
  onAddSelected,
  alreadyAddedIds
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const selectionMode = !!onAddSelected;

  // Local filter state (resets each time the dialog opens)
  const [searchQuery, setSearchQuery] = useState('');
  const [type, setType] = useState<QuestionType | ''>('');
  const [importance, setImportance] = useState<QuestionImportance | ''>('');
  const [chapterId, setChapterId] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 350);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [results, setResults] = useState<Question[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Selection survives pagination — users often pick a few from page 1, then
  // change filters and pick more. Keyed by question id to dodge equality
  // pitfalls and to keep the value bag (full Question) ready for the parent's
  // onAddSelected callback.
  const [picked, setPicked] = useState<Map<string, Question>>(new Map());

  useEffect(() => {
    if (open) {
      // Each open is a fresh search; don't carry stale filters across banks.
      setSearchQuery('');
      setType('');
      setImportance('');
      setChapterId('');
      setPage(0);
      setRowsPerPage(25);
      setResults([]);
      setTotalCount(0);
      setPicked(new Map());
    }
  }, [open, questionBankId]);

  // Whenever filters change, reset back to page 1 so the user doesn't end up
  // looking at "page 4 of nothing".
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, type, importance, chapterId]);

  // Only the newest request may fill the list; a slower older one is dropped.
  const resultsRequest = useRef(0);

  const fetchResults = useCallback(async () => {
    if (!open || !questionBankId) return;
    const request = ++resultsRequest.current;
    try {
      setLoading(true);
      const data = await questionPaperService.getQuestionsByBank(questionBankId, {
        search: debouncedSearch || undefined,
        type: type || undefined,
        importance: importance || undefined,
        chapterId: chapterId || undefined,
        page: page + 1,
        limit: rowsPerPage
      });
      if (request !== resultsRequest.current) return;
      setResults(data.questions);
      setTotalCount(data.pagination?.totalItems || 0);
    } catch (err: any) {
      if (request !== resultsRequest.current) return;
      console.error('Bank-wide search error:', err);
      enqueueSnackbar(err.message || 'Failed to search questions', { variant: 'error' });
    } finally {
      if (request === resultsRequest.current) setLoading(false);
    }
  }, [open, questionBankId, debouncedSearch, type, importance, chapterId, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Map chapterId → chapter name for the result rows so we can show where each
  // question lives. Falls back to whatever `populate` returned on the row.
  const chapterNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of chapters) m.set(c._id, c.name);
    return m;
  }, [chapters]);

  const togglePicked = (q: Question) => {
    setPicked(prev => {
      const next = new Map(prev);
      if (next.has(q._id)) next.delete(q._id);
      else next.set(q._id, q);
      return next;
    });
  };

  const renderQuestionLine = (q: Question) => {
    const chapter = q.chapterId as any;
    const chapterName = typeof chapter === 'object' && chapter?.name
      ? chapter.name
      : chapterNameById.get(typeof chapter === 'string' ? chapter : chapter?._id) || 'Unknown chapter';
    const isPicked = picked.has(q._id);
    const isAlreadyAdded = !!alreadyAddedIds?.has(q._id);

    return (
      <ListItem
        key={q._id}
        divider
        alignItems="flex-start"
        sx={{
          py: 1.5,
          cursor: selectionMode && !isAlreadyAdded ? 'pointer' : 'default',
          // Visually mute rows already in the section so users see they're
          // present without disabling click — a re-click is a no-op anyway.
          opacity: isAlreadyAdded ? 0.55 : 1,
          ...(selectionMode && !isAlreadyAdded && {
            '&:hover': { bgcolor: 'action.hover' }
          })
        }}
        onClick={() => {
          if (!selectionMode || isAlreadyAdded) return;
          togglePicked(q);
        }}
      >
        {selectionMode && (
          <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
            <Checkbox
              edge="start"
              checked={isPicked || isAlreadyAdded}
              disabled={isAlreadyAdded}
              tabIndex={-1}
              disableRipple
              onClick={(e) => e.stopPropagation()}
              onChange={() => togglePicked(q)}
            />
          </ListItemIcon>
        )}

        {/* Row body — same shape as QuestionBankBrowser cards so the two
            "pick a question" surfaces look like cousins. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
            <MathTextRenderer text={q.questionText} />
          </Typography>

          {q.type === QuestionType.MCQ && q.options && q.options.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              {q.options.map((opt, idx) => (
                <Typography
                  key={idx}
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontStyle: 'italic' }}
                >
                  {String.fromCharCode(65 + idx)}) <MathTextRenderer text={opt} />
                </Typography>
              ))}
            </Box>
          )}

          {q.subParts && q.subParts.length > 0 && (
            <Box sx={{ mt: 0.5, ml: 1.5 }}>
              {q.subParts.map((part, idx) => (
                <Typography
                  key={idx}
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', lineHeight: 1.5 }}
                >
                  <Box component="span" sx={{ fontWeight: 600, color: 'primary.main', mr: 0.5 }}>
                    ({ROMAN_NUMERALS[idx] || idx + 1})
                  </Box>
                  <MathTextRenderer text={part.text} />
                </Typography>
              ))}
            </Box>
          )}

          {/* Meta chips row (mirrors QuestionBankBrowser) plus chapter name
              on the right and the "Already in section" indicator. */}
          <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center" mt={0.75}>
            <Chip
              label={q.type}
              size="small"
              sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
            />
            <Chip
              label={q.importance}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                '& .MuiChip-label': { px: 0.75 },
                bgcolor: IMPORTANCE_COLOR[q.importance] + '22',
                color: IMPORTANCE_COLOR[q.importance],
                fontWeight: 600
              }}
            />
            {q.metadata?.marks !== undefined && (
              <Chip
                label={`${q.metadata.marks} mark${q.metadata.marks === 1 ? '' : 's'}`}
                size="small"
                sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
              />
            )}
            <AnswerBadge dense answer={q.answer} answerImages={q.answerImages} />
            {q.metadata?.tags?.map((tag, idx) => (
              <Chip
                key={`tag-${idx}`}
                label={tag}
                size="small"
                variant="outlined"
                color="primary"
                sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
              />
            ))}
            {q.metadata?.pastPaperReferences?.map((ref, idx) => (
              <Chip
                key={`past-${idx}`}
                label={`Past: ${ref}`}
                size="small"
                variant="outlined"
                color="secondary"
                sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
              />
            ))}
            {isAlreadyAdded && (
              <Chip
                label="Already in section"
                size="small"
                sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
              />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {chapterName}
            </Typography>
          </Box>
        </Box>
      </ListItem>
    );
  };

  const handleAddConfirm = () => {
    if (!onAddSelected || picked.size === 0) return;
    onAddSelected(Array.from(picked.values()));
    setPicked(new Map());
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
    >
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', pb: 1.5 }}>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={700}>
            {selectionMode ? 'Add questions from bank' : 'Search across this bank'}
          </Typography>
          {bankLabel && (
            <Typography variant="caption" color="text.secondary">{bankLabel}</Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" edge="end"><Close /></IconButton>
      </DialogTitle>

      <Box sx={{ height: 3, position: 'relative' }}>
        {loading && (
          <LinearProgress
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3 }}
          />
        )}
      </Box>

      <DialogContent sx={{ pt: 2, opacity: loading ? 0.75 : 1, transition: 'opacity 150ms ease' }}>
        {/* Filter bar */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search question text, options, tags…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flex: '1 1 240px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select MenuProps={elegantMenuProps} label="Type" value={type} onChange={(e) => setType(e.target.value as any)}>
              <MenuItem value=""><em>Any</em></MenuItem>
              {Object.values(QuestionType).map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Importance</InputLabel>
            <Select MenuProps={elegantMenuProps} label="Importance" value={importance} onChange={(e) => setImportance(e.target.value as any)}>
              <MenuItem value=""><em>Any</em></MenuItem>
              {Object.values(QuestionImportance).map(i => (
                <MenuItem key={i} value={i}>{i}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {chapters.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Chapter</InputLabel>
              <Select MenuProps={elegantMenuProps} label="Chapter" value={chapterId} onChange={(e) => setChapterId(e.target.value)}>
                <MenuItem value=""><em>All chapters</em></MenuItem>
                {chapters.map(c => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Divider />

        {/* Results */}
        {results.length === 0 && !loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {debouncedSearch || type || importance || chapterId
                ? 'No questions match your filters.'
                : 'Start typing to search across every chapter of this bank.'}
            </Typography>
          </Box>
        ) : results.length === 0 && loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <List dense disablePadding>
            {results.map(renderQuestionLine)}
          </List>
        )}

        {totalCount > 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        )}
      </DialogContent>

      {selectionMode && (
        <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', px: 3, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            {picked.size === 0
              ? 'Select questions to add to the section.'
              : `${picked.size} selected`}
          </Typography>
          <Button onClick={onClose} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddConfirm}
            disabled={picked.size === 0}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Add {picked.size > 0 ? `${picked.size} ` : ''}to section
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BankWideSearchDialog;
